import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import styled from "styled-components";
import ProfileContext from "common/ProfileContext";
import { useGeminiClient } from "./GeminiContext";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import CheckLicenseFirst from "common/CheckLicenseFirst";
import SelectInput from "common/SelectInput";

const MAX_PROMPT_LENGTH = 1500;

const Box = styled.div`
  position: fixed;
  background: #fff;
  color: #111;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  user-select: none;
  z-index: 999;
  min-width: 300px;

  transform: ${({ open }) => (open ? "scale(1)" : "scale(0.95)")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  transition: transform 0.3s ease, opacity 0.3s ease;

  &.dark {
    background: #1e1e1e;
    color: #e6e6e6;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
  }

  &.floating {
    border-radius: 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f2f2f2;
  cursor: grab;

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
  }

  .rotate-right {
    transform: rotate(90deg) !important;
  }

  .rotate-left {
    transform: rotate(-90deg) !important;
  }

  .dark & {
    background: #2a2a2a;
    color: #ffffff;
    border-bottom: 1px solid #3a3a3a;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chat-messages {
    user-select: text;
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    font-size: 0.9rem;

    ::-webkit-scrollbar {
      background: var(--scrollbar-color);
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb-color);
    }
  }

  .message {
    margin-bottom: 10px;
  }

  .user {
    text-align: right;
    color: #0078d7;
  }

  .bot {
    text-align: left;
  }

  .chat-input {
    display: flex;
    gap: 5px;
    padding: 20px 40px 20px 20px;
    align-items: start;
  }

  .input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .chat-input textarea {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 8px;
    outline: none;
    color: #111;
    background: #fff;
    height: 70px;
    width: 100%;
    resize: none;
    font-size: 14px;
  }

  .char-counter {
    text-align: right;
    font-size: 12px;
    margin-top: 4px;
    color: #888;
    font-family: monospace;
  }

  .char-counter.warning {
    color: #d97706;
  }

  .char-counter.limit {
    color: #dc2626;
    font-weight: 600;
  }

  .dark & .chat-input textarea {
    background: #2a2a2a !important;
    color: #ffffff !important;
    border: 1px solid #444 !important;
  }

  .dark .char-counter {
    color: #aaa;
  }

  .dark & .chat-input textarea::placeholder {
    color: #aaa !important;
  }

  .chat-input textarea::placeholder {
    color: #888;
  }

  .typing {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    user-select: none;
    background: rgba(0, 0, 0, 0.03);
    animation: typingFadeIn 0.25s ease;
  }

  .dark .typing {
    background: rgba(255, 255, 255, 0.05);
  }

  .dots {
    display: inline-flex;
    gap: 6px;
  }

  .dot {
    width: 9px;
    height: 9px;
    background: #999;
    border-radius: 50%;
    animation: dotPop 1s infinite ease-in-out;
  }

  .dot:nth-child(2) {
    animation-delay: 0.17s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.32s;
  }

  .wait-message {
    font-size: 13px;
    color: #777;
    white-space: nowrap;
    animation: messageFade 0.35s ease;
  }

  .dark .wait-message {
    color: #bbb;
  }

  @keyframes messageFade {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes typingFadeIn {
    from {
      opacity: 0;
      transform: translateY(3px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes dotPop {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.5;
    }
    20% {
      transform: translateY(-5px) scale(1.2);
      opacity: 1;
    }
    40% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.5;
    }
  }

  .dark & {
    background: #1e1e1e;
  }

  .dark .chat-messages {
    background: #1e1e1e;
    color: #e6e6e6;
  }

  .dark .bot {
    color: #e6e6e6;
  }

  .dark .user {
    color: #4da3ff;
  }

  .dark .chat-input textarea {
    background: #2a2a2a;
    color: #ffffff;
    border: 1px solid #444;
  }

  .dark .chat-input textarea::placeholder {
    color: #aaa;
  }

  .dark .dot {
    background: #bbb;
  }

  @keyframes dotPop {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
    20% {
      transform: translateY(-6px) scale(1.18);
      opacity: 1;
    }
    40% {
      transform: translateY(0) scale(1);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
  }
`;

const FooterResize = styled.div`
  position: absolute;
  bottom: 0px;
  right: 4px;
  cursor: nwse-resize;
`;

const Title = styled.label`
  font-family: "AllianceNo2-SemiBold" !important;
  color: var(--title-color) !important;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  margin-bottom: 0;
  cursor: grab;

  .dark & {
    color: #ffffff !important;
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
`;

const WAIT_MESSAGE_1 = "Please wait...";
const WAIT_MESSAGE_2 = "Accessing official documentation, please wait...";
const WAIT_MESSAGE_3 = "Elaborating your response, please wait...";
const WAIT_MESSAGE_4 =
  "It is taking longer than expected, but we are working on it, please wait...";
const WAIT_MESSAGE_1_TIMEOUT = 5000;
const WAIT_MESSAGE_2_TIMEOUT = 5000;
const WAIT_MESSAGE_3_TIMEOUT = 10000;
const WAIT_MESSAGE_4_TIMEOUT = 30000;

function getDimensions() {
  const ww = window.innerWidth;
  const wh = window.innerHeight;
  const wideScreen = ww > 1170;
  const menuWidth = wideScreen ? 300 : 0;
  const width = wideScreen ? ww / 4 - 15 : Math.min(ww * 0.8, 1600);
  const height = wideScreen ? wh : Math.min(wh * 0.9, 2000);

  return {
    width,
    height,
    top: wideScreen ? 0 : wh / 2 - height / 2,
    left: wideScreen ? (ww / 4) * 3 : (ww - menuWidth) / 2 - width / 2 - 5,
    wideScreen,
  };
}

export default function Chat({ open, onToggle, onSide }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hello! How can I help you?" },
  ]);
  const [writing, setWriting] = useState(false);
  const messagesEndRef = useRef(null);

  const inputLength = input.length;
  const limitReached = inputLength > MAX_PROMPT_LENGTH;
  const nearLimit = inputLength > MAX_PROMPT_LENGTH * 0.85;

  const { width, height, top, left, wideScreen } = getDimensions();

  const [boxPos, setBoxPos] = useState({
    top,
    left,
  });
  const [boxSize, setBoxSize] = useState({
    width,
    height,
  });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [farAway, setFarAway] = useState(true);
  const [waitMessage, setWaitMessage] = useState(null);
  const [floating, setFloating] = useState(false);
  const [mode, setMode] = useState("flash");

  const message = useRef("");
  const dragStart = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const chatMessagesRef = useRef();

  const profile = useContext(ProfileContext);
  const geminiClient = useGeminiClient();
  const navigate = useNavigate();

  const onMouseMove = useCallback(
    (e) => {
      if (dragging) {
        const deltaX = e.clientX - dragStart.current.x;
        const deltaY = e.clientY - dragStart.current.y;
        setBoxPos({
          top: Math.min(
            Math.max(0, dragStart.current.top + deltaY),
            window.innerHeight - 45,
          ),
          left: Math.min(
            Math.max(0, dragStart.current.left + deltaX),
            window.innerWidth - 45,
          ),
        });
      }
      if (resizing) {
        const deltaX = e.clientX - resizeStart.current.x;
        const deltaY = e.clientY - resizeStart.current.y;
        setBoxSize({
          width: Math.max(200, resizeStart.current.width + deltaX),
          height: Math.max(200, resizeStart.current.height + deltaY),
        });
      }
    },
    [dragging, resizing],
  );

  const onMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
  }, []);

  const resetDimensions = useCallback(() => {
    const { width, height, top, left, wideScreen } = getDimensions();

    setBoxPos({
      top,
      left,
    });
    setBoxSize({
      width,
      height,
    });
    setFloating(!wideScreen);
  }, [getDimensions]);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    window.addEventListener("resize", resetDimensions);
    return () => {
      window.removeEventListener("resize", resetDimensions);
    };
  }, [resetDimensions]);

  useEffect(() => {
    scrollToBottom();
    message.current = "";
  }, [messages]);

  useEffect(() => {
    if (!geminiClient) return;

    if (open) {
      geminiClient.listen((message) => {
        setMessages((prev) => [...prev, { role: "bot", text: message }]);
        setWriting(false);
      });
      setTimeout(() => scrollToBottom(), 250);
    } else {
      setWriting(false);
      geminiClient.stopListen();
    }
  }, [open, geminiClient]);

  useEffect(() => {
    if (!open || !chatMessagesRef.current) return;

    const handleClick = (e) => {
      if (e.target.tagName === "A") {
        e.preventDefault();
        const url = new URL(e.target.href);
        if (url.hostname === window.location.hostname) {
          navigate(url.pathname);
        } else {
          window.open(e.target.href, "_blank");
        }
      }
    };

    chatMessagesRef.current.addEventListener("click", handleClick, true);

    return () =>
      chatMessagesRef.current.removeEventListener("click", handleClick, true);
  }, [chatMessagesRef.current, open]);

  useEffect(() => {
    if (open) {
      resetDimensions();
      setFarAway(false);
    } else {
      setTimeout(() => {
        setFarAway(true);
      }, 300);
    }
  }, [open]);

  useEffect(() => {
    let timeoutId;
    if (writing) {
      timeoutId = setTimeout(() => {
        setWaitMessage(WAIT_MESSAGE_1);
      }, WAIT_MESSAGE_1_TIMEOUT);
    } else {
      clearTimeout(timeoutId);
      setWaitMessage(null);
    }

    return () => clearTimeout(timeoutId);
  }, [writing]);

  useEffect(() => {
    let timeoutId;
    switch (waitMessage) {
      case WAIT_MESSAGE_1:
        timeoutId = setTimeout(() => {
          setWaitMessage(WAIT_MESSAGE_2);
        }, WAIT_MESSAGE_2_TIMEOUT);
        break;
      case WAIT_MESSAGE_2:
        timeoutId = setTimeout(() => {
          setWaitMessage(WAIT_MESSAGE_3);
        }, WAIT_MESSAGE_3_TIMEOUT);
        break;
      case WAIT_MESSAGE_3:
        timeoutId = setTimeout(() => {
          setWaitMessage(WAIT_MESSAGE_4);
        }, WAIT_MESSAGE_4_TIMEOUT);
        break;
    }

    return () => clearTimeout(timeoutId);
  }, [waitMessage]);

  useEffect(() => {
    onSide(open && !floating && wideScreen);
  }, [floating, open, onSide, wideScreen]);

  useEffect(() => {
    if (dragging || resizing) {
      onSide(false);
      setFloating(true);
    }
  }, [dragging, resizing, onSide]);

  const sendMessage = async () => {
    if (!input.trim() || limitReached) return;
    const userMessage = { role: "user", text: input };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setWriting(true);
    geminiClient.sendMessage(input, messages, mode);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value.slice(0, MAX_PROMPT_LENGTH));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
  };

  const onMouseDown = (e) => {
    setDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      top: boxPos.top,
      left: boxPos.left,
    };
    e.preventDefault();
  };

  const onResizeMouseDown = (e) => {
    setResizing(true);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: boxSize.width,
      height: boxSize.height,
    };
    e.stopPropagation();
  };

  const onClose = () => {
    setMessages([{ role: "bot", text: "Hello again! How can I help you?" }]);
    onToggle();
  };

  if (!profile) return null;

  const isDark = login.isTheme("dark");
  const radioClassName = isDark ? "radio-col-white" : "radio-col-black";

  return (
    <Box
      top={boxPos.top}
      left={farAway ? -3000 : boxPos.left}
      width={boxSize.width}
      height={boxSize.height}
      open={open}
      className={`${isDark ? "dark" : ""}${floating ? " floating" : ""}`}
    >
      <Header onMouseDown={onMouseDown}>
        <div className="left">
          <AutoAwesomeIcon className="material-icons font-size-80" />
          <Title> BQN AI Assistant </Title>
        </div>
        <div className="right">
          {wideScreen && floating && (
            <button
              onClick={resetDimensions}
              title="Quarter Right"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <VerticalAlignTopIcon className="rotate-right" />
            </button>
          )}
          <button onClick={onToggle} onMouseDown={(e) => e.stopPropagation()}>
            <MinimizeIcon />
          </button>
          <button onClick={onClose} onMouseDown={(e) => e.stopPropagation()}>
            <CloseIcon />
          </button>
        </div>
      </Header>

      <Content>
        <CheckLicenseFirst reloadSignal={open}>
          <div ref={chatMessagesRef} className="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`message ${m.role}`}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.text) }}
              ></div>
            ))}
            {writing && (
              <div className="typing">
                <div className="dots">
                  <div className="dot" aria-hidden="true"></div>
                  <div className="dot" aria-hidden="true"></div>
                  <div className="dot" aria-hidden="true"></div>
                </div>
                <span key={waitMessage} className="wait-message">
                  {waitMessage}
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <div className="input-wrapper">
              <textarea
                type="text"
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                maxLength={MAX_PROMPT_LENGTH}
              />

              <StatusBar>
                <SelectInput
                  options={[
                    { label: "Normal", value: "flash" },
                    { label: "Fast", value: "lite" },
                    { label: "Quality", value: "pro" },
                  ]}
                  defaultValue={mode}
                  onChange={(event) => setMode(event.target.value)}
                />

                <div
                  className={`char-counter ${
                    limitReached ? "limit" : nearLimit ? "warning" : ""
                  }`}
                >
                  {inputLength} / {MAX_PROMPT_LENGTH}
                </div>
              </StatusBar>
            </div>

            <button
              onClick={sendMessage}
              disabled={!input.trim() || limitReached}
              type="button"
              className="btn btn-link waves-effect"
            >
              <SendIcon />
            </button>
          </div>
        </CheckLicenseFirst>
      </Content>

      <FooterResize onMouseDown={onResizeMouseDown}>
        <DragIndicatorIcon />
      </FooterResize>
    </Box>
  );
}
